import { useState, useCallback } from "react";
import useApiCall from "./useApiCall";

/**
 * Custom hook to handle PUT requests using useApiCall.
 *
 * @param {string} url - The API endpoint URL where the PUT request will be sent.
 * @param {object} tokens - The authentication tokens for accessing the API.
 * @returns {object} - Object containing response data,
 *  loading state, error state, and request function.
 */
const usePutRequest = (url, tokens) => {
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const { makeRequest, isLoading } = useApiCall();

  /**
   * Function to send a PUT request to the given URL.
   *
   * @param {object} body - The request body to be sent with the PUT request.
   * @param {string} [urlParam=""] - Optional URL parameter to append.
   * @returns {Promise<any>} - The response data from the API.
   * @throws {Error} - Throws an error if the PUT request fails.
   */
  const sendPutRequest = useCallback(
    async (body, urlParam = "") => {
      setError(null);

      try {
        const response = await makeRequest(url + urlParam, {
          method: "PUT",
          authTokens: tokens,
          body,
        });

        setData(response.response || []);
        return response.response;
      } catch (err) {
        setError(err);
        throw err;
      }
    },
    [url, tokens, makeRequest]
  );

  return { data, loading: isLoading, error, sendPutRequest };
};

export default usePutRequest;
